import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

/**
 * Microsoft 365 OAuth callback landing page. Microsoft bounces here with
 * ?code=<auth_code>&state=<jwt>. We POST { code } to /api/microsoft-auth/callback
 * authenticated with the JWT from state, then return to Settings → Email.
 */
export default function OAuthMicrosoftCallback() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('exchanging');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errorParam = params.get('error_description') || params.get('error');

    if (errorParam) { setPhase('error'); setMessage(`Microsoft denied access: ${errorParam}`); return; }
    if (!code) { setPhase('error'); setMessage('Missing authorization code in callback URL.'); return; }

    const token = state || localStorage.getItem('token');
    if (!token) { setPhase('error'); setMessage('You need to be logged in to complete the connect. Please log in and reconnect.'); return; }

    axios
      .post(`${API_URL}/api/microsoft-auth/callback`, { code }, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setPhase('success');
        setMessage(`Microsoft 365 connected${res.data?.email ? ` as ${res.data.email}` : ''}. Bouncing back to Settings…`);
        setTimeout(() => navigate('/integrations?open=email_sending'), 1500);
      })
      .catch((err) => {
        setPhase('error');
        setMessage(err.response?.data?.error || err.message || 'Token exchange failed.');
      });
  }, [navigate]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-4 p-8 text-center">
          {phase === 'exchanging' && (
            <>
              <Loader2 className="mx-auto size-8 animate-spin text-primary" />
              <h2 className="text-base font-semibold">Connecting Microsoft 365…</h2>
              <p className="text-sm text-muted-foreground">Exchanging authorization code for tokens. This takes a second.</p>
            </>
          )}
          {phase === 'success' && (
            <>
              <CheckCircle2 className="mx-auto size-8 text-success" />
              <h2 className="text-base font-semibold">Connected</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
            </>
          )}
          {phase === 'error' && (
            <>
              <AlertCircle className="mx-auto size-8 text-destructive" />
              <h2 className="text-base font-semibold">Couldn't finish OAuth</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
              <div className="flex justify-center gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/integrations?open=email_sending')}>Back to Integrations</Button>
                {/* Retry as the user the flow was STARTED for (the state JWT),
                    not whoever is logged into this browser - otherwise an admin
                    retrying a connect-for-a-teammate silently connects their
                    own mailbox and reports success. */}
                <Button size="sm" onClick={() => {
                  const t = new URLSearchParams(window.location.search).get('state')
                    || localStorage.getItem('token');
                  window.location.href = `${API_URL}/api/microsoft-auth/auth-url?token=${encodeURIComponent(t || '')}`;
                }}>Try again</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
